import React, { useState, useEffect } from "react";
import { Alert, Tabs, Spin, Select } from "antd";
import { LoadingOutlined } from "@ant-design/icons";

import SupportInterventionStats from "../../support/components/SupportInterventionStats";
import SLAViolationOrders from "../../support/components/SLAViolationOrders";
import OrderDetailsModal from "../../support/components/OrderDetailsModal";
import InterventionActionsModal from "../../support/components/InterventionActionsModal";
import {
  showErrorNotification,
  showSuccessNotification,
} from "../../../common/utils/notification";
import { _get_support_staffs } from "../../../common/axios_services";

import useSupportData from "../../support/hooks/useSupportData";

const AdminInterventionsContent = ({
  useOwnLayout = false,
  userRole = "ADMIN",
}) => {
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderModalVisible, setOrderModalVisible] = useState(false);
  const [interventionModalVisible, setInterventionModalVisible] =
    useState(false);
  const [activeTab, setActiveTab] = useState("scheduled");

  const [selectedSupportManager, setSelectedSupportManager] = useState(null);
  const [selectedSupportOfficer, setSelectedSupportOfficer] = useState(null);
  const [supportManagers, setSupportManagers] = useState([]);
  const [supportOfficers, setSupportOfficers] = useState([]);
  const [loadingFilters, setLoadingFilters] = useState(false);

  const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

  const { slaViolations, loading, error, refreshData, fetchSlaViolations } =
    useSupportData();

  useEffect(() => {
    const fetchSupportManagers = async () => {
      if (userRole !== "ADMIN" && userRole !== "SE") return;

      setLoadingFilters(true);
      try {
        const response = await _get_support_staffs({
          page: 1,
          limit: 100,
          role: "support_manager",
        });

        const managers =
          response?.data?.data?.data || response?.data?.data || [];
        setSupportManagers(
          managers
            .filter(
              (x) => x.role == "SUPPORT_MANAGER" || x.role == "support_manager",
            )
            .map((sm) => ({
              label:
                `${sm.firstName || ""} ${sm.lastName || ""}`.trim() ||
                sm.email ||
                "Unknown",
              value: sm.id,
            })),
        );
      } catch (error) {
        console.error("Error fetching support managers:", error);
      } finally {
        setLoadingFilters(false);
      }
    };

    fetchSupportManagers();
  }, [userRole]);

  useEffect(() => {
    const fetchSupportOfficers = async () => {
      if (!selectedSupportManager && userRole === "SE") {
        setSupportOfficers([]);
        return;
      }

      if (userRole !== "ADMIN" && userRole !== "SE") return;

      setLoadingFilters(true);
      try {
        const response = await _get_support_staffs({
          page: 1,
          limit: 100,
          role: "support_officer",
        });

        const officers =
          response?.data?.data?.data || response?.data?.data || [];
        setSupportOfficers(
          officers
            .filter(
              (x) => x.role == "SUPPORT_OFFICER" || x.role == "support_officer",
            )
            .map((so) => ({
              label:
                `${so.firstName || ""} ${so.lastName || ""}`.trim() ||
                so.email ||
                "Unknown",
              value: so.id,
            })),
        );
      } catch (error) {
        console.error("Error fetching support officers:", error);
      } finally {
        setLoadingFilters(false);
      }
    };

    fetchSupportOfficers();
  }, [selectedSupportManager, userRole]);

  useEffect(() => {
    if (userRole === "ADMIN" || userRole === "SE") {
      fetchSlaViolations(1, 20, selectedSupportManager, selectedSupportOfficer);
    }
  }, [selectedSupportManager, selectedSupportOfficer, userRole]);

  const handleSMChange = (value) => {
    setSelectedSupportManager(value);
    setSelectedSupportOfficer(null);
  };

  const handleSOChange = (value) => {
    setSelectedSupportOfficer(value);
  };

  const handleClearFilters = () => {
    setSelectedSupportManager(null);
    setSelectedSupportOfficer(null);
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setOrderModalVisible(true);
  };

  const handleOpenIntervention = () => {
    setOrderModalVisible(false);
    setInterventionModalVisible(true);
  };

  const getViolationTypeWithActor = () => {
    if (!activeTab || !selectedOrder) return activeTab;

    if (activeTab === "not-picked" || activeTab === "not-delivered") {
      const isDriver = selectedOrder.deliveryType === "DELIVERY";
      return `${activeTab}-${isDriver ? "driver" : "buyer"}`;
    }
    return activeTab;
  };

  const handleRefresh = async () => {
    await refreshData();
    showSuccessNotification("Dashboard refreshed");
  };

  if (error && !slaViolations) {
    return (
      <Alert
        message="Error Loading Support Portal"
        description={error}
        type="error"
        showIcon
      />
    );
  }

  const totalViolations =
    (slaViolations?.scheduled?.length || 0) +
    (slaViolations?.notPicked?.length || 0) +
    (slaViolations?.notDelivered?.length || 0);

  const showFilters = userRole === "ADMIN" || userRole === "SE" || userRole === "SM";

  return (
    <Spin spinning={loading || loadingFilters} indicator={antIcon}>
      <div className={useOwnLayout ? "pb-40" : ""}>
        <div className="main_container_title_div mb-5 mt-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">
            Support Portal - SLA Dashboard
          </h1>
          <p className="text-gray-600 text-xl sm:text-base">
            Monitor and manage SLA violations
          </p>
        </div>

        {showFilters && (
          <div className="mb-4 flex flex-wrap gap-4 items-center bg-gray-50 p-4 rounded">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium mb-1">
                Support Manager
              </label>
              <Select
                placeholder="Select Support Manager"
                value={selectedSupportManager || undefined}
                onChange={handleSMChange}
                allowClear
                onClear={handleClearFilters}
                options={supportManagers}
                style={{ width: "100%" }}
                loading={loadingFilters}
              />
            </div>
          </div>
        )}

        {totalViolations > 0 && (
          <Alert
            message={`${totalViolations} orders with SLA violations`}
            description="These orders require immediate attention. Click on violations below to take action."
            type="warning"
            showIcon
            style={{ marginBottom: "20px" }}
          />
        )}

        <div className="mb-8 -mx-4 px-4">
          <SupportInterventionStats stats={slaViolations} loading={loading} />
        </div>

        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          type="card"
          className="support-tabs"
        >
          <Tabs.TabPane
            tab={`Scheduled (${slaViolations?.scheduled?.length || 0})`}
            key="scheduled"
          >
            <div className="mt-6">
              <SLAViolationOrders
                orders={slaViolations?.scheduled || []}
                violationType="SCHEDULED"
                loading={loading}
                onViewOrder={handleViewOrder}
                onRefresh={handleRefresh}
              />
            </div>
          </Tabs.TabPane>

          <Tabs.TabPane
            tab={`Not Picked (${slaViolations?.notPicked?.length || 0})`}
            key="not-picked"
          >
            <div className="mt-6">
              <SLAViolationOrders
                orders={slaViolations?.notPicked || []}
                violationType="NOT_PICKED"
                loading={loading}
                onViewOrder={handleViewOrder}
                onRefresh={handleRefresh}
              />
            </div>
          </Tabs.TabPane>

          <Tabs.TabPane
            tab={`Not Delivered (${slaViolations?.notDelivered?.length || 0})`}
            key="not-delivered"
          >
            <div className="mt-6">
              <SLAViolationOrders
                orders={slaViolations?.notDelivered || []}
                violationType="NOT_DELIVERED"
                loading={loading}
                onViewOrder={handleViewOrder}
                onRefresh={handleRefresh}
              />
            </div>
          </Tabs.TabPane>
        </Tabs>

        <OrderDetailsModal
          visible={orderModalVisible}
          order={selectedOrder}
          onCancel={() => setOrderModalVisible(false)}
          loading={loading}
          tab={activeTab}
          onIntervention={handleOpenIntervention}
        />

        <InterventionActionsModal
          visible={interventionModalVisible}
          order={selectedOrder}
          violationType={getViolationTypeWithActor()}
          onCancel={() => setInterventionModalVisible(false)}
          onSuccess={() => {
            setInterventionModalVisible(false);
            setSelectedOrder(null);
            refreshData();
          }}
        />
      </div>
    </Spin>
  );
};

export default AdminInterventionsContent;
